<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  KIND_LABELS,
  STATUS_LABELS,
  createLocation,
  deleteLocation,
  listLocations,
  updateLocation,
  type Location,
  type LocationInput,
  type LocationKind,
} from '../api/locations'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const rows = ref<Location[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const warehouses = ref<Warehouse[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  zone: '',
  kind: '',
  status: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<Location | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<Omit<LocationInput, 'warehouse_id'> & { warehouse_id?: number }>({
  warehouse_id: undefined,
  code: '',
  name: '',
  zone: '',
  kind: 'shelf',
  status: 'active',
})

/** 仓库下拉与表格展示共用：id → 「编码 名称」 */
function whLabel(id: number): string {
  const w = warehouses.value.find((item) => item.id === id)
  return w ? `${w.code} ${w.name}`.trim() : `#${id}`
}

const codeError = computed(() => (form.code.trim() ? '' : '请输入库位编码'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listLocations({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      zone: query.zone.trim() || undefined,
      kind: query.kind || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadWarehouses(): Promise<void> {
  refError.value = null
  try {
    const res = await listWarehouses({ page: 1, size: 100 })
    warehouses.value = res.list
    // 新建默认选中第一个仓库（通常就是默认仓）
    if (!form.warehouse_id && res.list.length) form.warehouse_id = res.list[0].id
  } catch (e) {
    // 仓库列表拿不到时不影响库位列表本身，但必须把原因露出来（如未选货主时后端回 422）
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadWarehouses()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  editing.value = null
  Object.assign(form, {
    warehouse_id: warehouses.value[0]?.id,
    code: '',
    name: '',
    zone: '',
    kind: 'shelf',
    status: 'active',
  })
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Location): void {
  editing.value = row
  Object.assign(form, {
    warehouse_id: row.warehouse_id,
    code: row.code,
    name: row.name,
    zone: row.zone,
    kind: row.kind,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (codeError.value) return
  saving.value = true
  formError.value = null
  const input: LocationInput = {
    warehouse_id: form.warehouse_id ?? 0,
    code: form.code.trim(),
    name: form.name.trim(),
    zone: form.zone.trim(),
    kind: form.kind,
    status: form.status,
  }
  try {
    if (editing.value) await updateLocation(editing.value.id, input)
    else await createLocation(input)
    formOpen.value = false
    await load(editing.value ? '库位已更新' : '库位已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Location): Promise<void> {
  notice.value = ''
  try {
    await deleteLocation(row.id)
    await load(`已删除库位「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-loc">
    <header class="yc-loc__bar">
      <el-input
        v-model="query.keyword"
        class="yc-loc__keyword"
        placeholder="库位编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-loc__wh" placeholder="全部仓库" clearable @change="search">
        <el-option v-for="w in warehouses" :key="w.id" :label="whLabel(w.id)" :value="w.id" />
      </el-select>
      <el-input v-model="query.zone" class="yc-loc__zone" placeholder="库区" clearable @keyup.enter="search" @clear="search" />
      <el-select v-model="query.kind" class="yc-loc__kind" placeholder="全部类型" clearable @change="search">
        <el-option v-for="(label, value) in KIND_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.status" class="yc-loc__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:location:create'" type="primary" @click="openCreate">新建库位</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-loc__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-loc__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-loc__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column label="仓库" min-width="140">
          <template #default="{ row }">{{ whLabel(row.warehouse_id) }}</template>
        </el-table-column>
        <el-table-column prop="code" label="库位编码" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="zone" label="库区" width="120" />
        <el-table-column label="类型" width="120">
          <template #default="{ row }">{{ KIND_LABELS[row.kind as LocationKind] ?? row.kind }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as Location['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'wms:location:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该库位？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:location:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有库位"
        description="库位是上架、拣货、盘点的基本单元，先建几个货架位开始。"
      >
        <el-button v-perm="'wms:location:create'" type="primary" @click="openCreate">新建库位</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-loc__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑库位 · ${editing.code}` : '新建库位'"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-loc__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="所属仓库">
          <el-select v-model="form.warehouse_id" class="yc-loc__wh">
            <el-option v-for="w in warehouses" :key="w.id" :label="whLabel(w.id)" :value="w.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="库位编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 A-01-03" />
          <span class="yc-loc__hint">同一货主内唯一，如 A 表示库区、01 排、03 位。</span>
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 A 区 1 排 3 位" />
        </el-form-item>

        <el-form-item label="库区">
          <el-input v-model="form.zone" maxlength="32" placeholder="如 A" />
        </el-form-item>

        <el-form-item label="类型">
          <el-select v-model="form.kind">
            <el-option v-for="(label, value) in KIND_LABELS" :key="value" :label="label" :value="value" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!codeError">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-loc {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__wh {
    width: 200px;
  }

  &__zone {
    width: 120px;
  }

  &__kind,
  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;
  }
}
</style>
